import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { obtenerDatosSeguimiento } from "../../services/api";
import ProponerCitaModal from "./ProponerCitaModal";

// Brand colors - LemmonTags theme
const COLORS = {
  primary: "#84cc16",
  primaryDark: "#65a30d",
  primaryLight: "#ecfccb",
  background: "#fafafa",
  cardBg: "#ffffff",
  textDark: "#1f2937",
  textMuted: "#6b7280",
  border: "#e5e7eb",
  danger: "#dc2626",
  dangerLight: "#fef2f2",
  warning: "#f59e0b",
  warningLight: "#fef3c7",
  info: "#3b82f6",
  infoLight: "#dbeafe"
};

// Opciones de período
const PERIODOS = [
  { value: 1, label: "1 mes" },
  { value: 3, label: "3 meses" },
  { value: 6, label: "6 meses" },
  { value: 12, label: "1 año" }
];

export default function VetPanelSeguimiento() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const vetId = searchParams.get("vetId") || "";
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [periodo, setPeriodo] = useState(3);

  // Datos de seguimiento
  const [datos, setDatos] = useState({
    proximasCitas: [],
    citasCanceladas: [],
    pacientesSinRegresar: [],
    resumen: { totalProximasCitas: 0, totalCanceladas: 0, totalSinRegresar: 0 }
  });

  // Tab activa para la sección de próximas citas
  const [tabProximas, setTabProximas] = useState("todos");

  // Modal de proponer cita
  const [showProponerModal, setShowProponerModal] = useState(false);
  const [pacienteSeleccionado, setPacienteSeleccionado] = useState(null);

  useEffect(() => {
    cargarDatos();
  }, [vetId, periodo]);

  async function cargarDatos() {
    if (!vetId) {
      setError("ID de veterinario no encontrado");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const resp = await obtenerDatosSeguimiento(vetId, periodo);
      if (resp.error) {
        setError(resp.error);
      } else {
        setDatos(resp.data);
      }
    } catch (err) {
      console.error("Error cargando datos:", err);
      setError("Error al cargar datos de seguimiento");
    } finally {
      setLoading(false);
    }
  }

  function handleBack() {
    navigate(`/vetPanel?token=${token}`);
  }

  function handleProponerCita(paciente) {
    setPacienteSeleccionado(paciente);
    setShowProponerModal(true);
  }

  function handleCitaPropuesta() {
    // Recargar datos después de proponer cita
    cargarDatos();
  }

  function formatearFecha(fecha) {
    if (!fecha) return "Sin fecha";
    try {
      const date = new Date(fecha + "T12:00:00");
      return date.toLocaleDateString("es-MX", {
        day: "numeric",
        month: "short",
        year: "numeric"
      });
    } catch {
      return fecha;
    }
  }

  // Filtrar próximas citas según tab activa
  const proximasFiltradas = datos.proximasCitas.filter(item => {
    if (tabProximas === "todos") return true;
    if (tabProximas === "vacuna") return item.tipo === "vacuna";
    if (tabProximas === "desparasitacion") return item.tipo === "desparasitacion";
    return true;
  });

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh",
        backgroundColor: COLORS.background,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <div className="text-center">
          <div className="spinner-border" style={{ color: COLORS.primary }} role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p style={{ color: COLORS.textMuted, marginTop: "12px" }}>Cargando datos de seguimiento...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: "100vh",
        backgroundColor: COLORS.background,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px"
      }}>
        <div className="text-center" style={{ maxWidth: "400px" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</div>
          <h5 style={{ color: COLORS.textDark }}>Error al cargar datos</h5>
          <p style={{ color: COLORS.textMuted }}>{error}</p>
          <button
            className="btn"
            style={{ backgroundColor: COLORS.primary, color: "white" }}
            onClick={handleBack}
          >
            Volver al panel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: COLORS.background, padding: "20px" }}>
      <div style={{ maxWidth: "900px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: "24px" }}>
          <button
            className="btn btn-sm"
            style={{
              backgroundColor: "transparent",
              color: COLORS.textMuted,
              border: "none",
              padding: "8px 0",
              marginBottom: "12px"
            }}
            onClick={handleBack}
          >
            ← Volver al panel
          </button>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h2 style={{ color: COLORS.textDark, fontWeight: 600, marginBottom: "4px" }}>
                Panel de Seguimiento
              </h2>
              <p style={{ color: COLORS.textMuted, margin: 0 }}>
                Metricas de retencion y oportunidades de contacto
              </p>
            </div>
            {/* Selector de período */}
            <div className="d-flex gap-2">
              {PERIODOS.map(p => (
                <button
                  key={p.value}
                  className="btn btn-sm"
                  style={{
                    backgroundColor: periodo === p.value ? COLORS.primary : COLORS.cardBg,
                    color: periodo === p.value ? "white" : COLORS.textDark,
                    border: `1px solid ${periodo === p.value ? COLORS.primary : COLORS.border}`,
                    fontWeight: periodo === p.value ? 600 : 400
                  }}
                  onClick={() => setPeriodo(p.value)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tarjetas de resumen */}
        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <div className="card border-0 shadow-sm h-100" style={{ backgroundColor: COLORS.cardBg }}>
              <div className="card-body text-center">
                <div style={{ fontSize: "36px", fontWeight: 700, color: COLORS.warning }}>
                  {datos.resumen.totalProximasCitas}
                </div>
                <p style={{ color: COLORS.textMuted, margin: 0, fontSize: "14px" }}>
                  Proximas citas necesarias
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card border-0 shadow-sm h-100" style={{ backgroundColor: COLORS.cardBg }}>
              <div className="card-body text-center">
                <div style={{ fontSize: "36px", fontWeight: 700, color: COLORS.danger }}>
                  {datos.resumen.totalCanceladas}
                </div>
                <p style={{ color: COLORS.textMuted, margin: 0, fontSize: "14px" }}>
                  Citas canceladas
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card border-0 shadow-sm h-100" style={{ backgroundColor: COLORS.cardBg }}>
              <div className="card-body text-center">
                <div style={{ fontSize: "36px", fontWeight: 700, color: COLORS.info }}>
                  {datos.resumen.totalSinRegresar}
                </div>
                <p style={{ color: COLORS.textMuted, margin: 0, fontSize: "14px" }}>
                  Pacientes sin regresar
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sección 1: Próximas consultas necesarias */}
        <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: COLORS.cardBg }}>
          <div className="card-header" style={{ backgroundColor: COLORS.warningLight, border: "none" }}>
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0" style={{ fontWeight: 600, color: COLORS.textDark }}>
                Pacientes que necesitan atencion
              </h6>
              <span className="badge" style={{ backgroundColor: COLORS.warning, color: "white" }}>
                {datos.proximasCitas.length}
              </span>
            </div>
          </div>
          <div className="card-body">
            {/* Tabs */}
            <div className="d-flex gap-2 mb-3">
              {[
                { key: "todos", label: "Todos" },
                { key: "vacuna", label: "Vacunacion" },
                { key: "desparasitacion", label: "Desparasitacion" }
              ].map(tab => (
                <button
                  key={tab.key}
                  className="btn btn-sm"
                  style={{
                    backgroundColor: tabProximas === tab.key ? COLORS.primary : "transparent",
                    color: tabProximas === tab.key ? "white" : COLORS.textMuted,
                    border: `1px solid ${tabProximas === tab.key ? COLORS.primary : COLORS.border}`
                  }}
                  onClick={() => setTabProximas(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {proximasFiltradas.length === 0 ? (
              <p style={{ color: COLORS.textMuted, textAlign: "center", padding: "20px 0" }}>
                No hay pacientes que necesiten atencion en este periodo
              </p>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr style={{ backgroundColor: COLORS.background }}>
                      <th style={{ fontSize: "13px", color: COLORS.textMuted, fontWeight: 600 }}>Mascota</th>
                      <th style={{ fontSize: "13px", color: COLORS.textMuted, fontWeight: 600 }}>Tipo</th>
                      <th style={{ fontSize: "13px", color: COLORS.textMuted, fontWeight: 600 }}>Vence</th>
                      <th style={{ fontSize: "13px", color: COLORS.textMuted, fontWeight: 600 }}>Dueno</th>
                      <th style={{ fontSize: "13px", color: COLORS.textMuted, fontWeight: 600 }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proximasFiltradas.map(item => (
                      <tr key={item.id}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {item.mascotaFoto ? (
                              <img
                                src={item.mascotaFoto}
                                alt={item.mascotaNombre}
                                style={{ width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover" }}
                              />
                            ) : (
                              <div style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                backgroundColor: COLORS.primaryLight,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "14px"
                              }}>
                                🐾
                              </div>
                            )}
                            <span style={{ fontWeight: 500, color: COLORS.textDark }}>{item.mascotaNombre}</span>
                          </div>
                        </td>
                        <td>
                          <span className="badge" style={{
                            backgroundColor: item.tipo === "vacuna" ? COLORS.infoLight : COLORS.primaryLight,
                            color: item.tipo === "vacuna" ? COLORS.info : COLORS.primaryDark,
                            fontSize: "12px"
                          }}>
                            {item.descripcion}
                          </span>
                        </td>
                        <td style={{ color: COLORS.textDark, fontSize: "14px" }}>
                          {formatearFecha(item.fechaVence)}
                        </td>
                        <td style={{ color: COLORS.textMuted, fontSize: "14px" }}>
                          {item.duenoNombre}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm"
                            style={{
                              backgroundColor: COLORS.primary,
                              color: "white",
                              fontSize: "12px",
                              padding: "4px 12px"
                            }}
                            onClick={() => handleProponerCita({
                              objetoId: item.objetoId,
                              mascotaNombre: item.mascotaNombre,
                              duenoId: item.duenoId,
                              duenoNombre: item.duenoNombre,
                              tipoSugerido: item.tipo === "vacuna" ? "Vacunacion" : "Desparasitacion"
                            })}
                          >
                            Proponer cita
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Sección 2: Citas canceladas */}
        <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: COLORS.cardBg }}>
          <div className="card-header" style={{ backgroundColor: COLORS.dangerLight, border: "none" }}>
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0" style={{ fontWeight: 600, color: COLORS.textDark }}>
                Citas canceladas
              </h6>
              <span className="badge" style={{ backgroundColor: COLORS.danger, color: "white" }}>
                {datos.citasCanceladas.length}
              </span>
            </div>
          </div>
          <div className="card-body">
            {datos.citasCanceladas.length === 0 ? (
              <p style={{ color: COLORS.textMuted, textAlign: "center", padding: "20px 0" }}>
                No hay citas canceladas en este periodo
              </p>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr style={{ backgroundColor: COLORS.background }}>
                      <th style={{ fontSize: "13px", color: COLORS.textMuted, fontWeight: 600 }}>Mascota</th>
                      <th style={{ fontSize: "13px", color: COLORS.textMuted, fontWeight: 600 }}>Fecha original</th>
                      <th style={{ fontSize: "13px", color: COLORS.textMuted, fontWeight: 600 }}>Motivo</th>
                      <th style={{ fontSize: "13px", color: COLORS.textMuted, fontWeight: 600 }}>Dueno</th>
                      <th style={{ fontSize: "13px", color: COLORS.textMuted, fontWeight: 600 }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.citasCanceladas.map(item => (
                      <tr key={item.id}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {item.mascotaFoto ? (
                              <img
                                src={item.mascotaFoto}
                                alt={item.mascotaNombre}
                                style={{ width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover" }}
                              />
                            ) : (
                              <div style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                backgroundColor: COLORS.dangerLight,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "14px"
                              }}>
                                🐾
                              </div>
                            )}
                            <span style={{ fontWeight: 500, color: COLORS.textDark }}>{item.mascotaNombre}</span>
                          </div>
                        </td>
                        <td style={{ color: COLORS.textDark, fontSize: "14px" }}>
                          {formatearFecha(item.fechaOriginal)}
                        </td>
                        <td style={{ color: COLORS.textMuted, fontSize: "14px" }}>
                          {item.motivo}
                        </td>
                        <td style={{ color: COLORS.textMuted, fontSize: "14px" }}>
                          {item.duenoNombre}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm"
                            style={{
                              backgroundColor: COLORS.danger,
                              color: "white",
                              fontSize: "12px",
                              padding: "4px 12px"
                            }}
                            onClick={() => handleProponerCita({
                              objetoId: item.objetoId,
                              mascotaNombre: item.mascotaNombre,
                              duenoId: item.ownerId,
                              duenoNombre: item.duenoNombre,
                              tipoSugerido: item.tipoConsulta || "Consulta"
                            })}
                          >
                            Reagendar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Sección 3: Pacientes sin regresar */}
        <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: COLORS.cardBg }}>
          <div className="card-header" style={{ backgroundColor: COLORS.infoLight, border: "none" }}>
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0" style={{ fontWeight: 600, color: COLORS.textDark }}>
                Pacientes sin visita reciente
              </h6>
              <span className="badge" style={{ backgroundColor: COLORS.info, color: "white" }}>
                {datos.pacientesSinRegresar.length}
              </span>
            </div>
          </div>
          <div className="card-body">
            {datos.pacientesSinRegresar.length === 0 ? (
              <p style={{ color: COLORS.textMuted, textAlign: "center", padding: "20px 0" }}>
                Todos tus pacientes han regresado recientemente
              </p>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr style={{ backgroundColor: COLORS.background }}>
                      <th style={{ fontSize: "13px", color: COLORS.textMuted, fontWeight: 600 }}>Mascota</th>
                      <th style={{ fontSize: "13px", color: COLORS.textMuted, fontWeight: 600 }}>Ultima visita</th>
                      <th style={{ fontSize: "13px", color: COLORS.textMuted, fontWeight: 600 }}>Que se hizo</th>
                      <th style={{ fontSize: "13px", color: COLORS.textMuted, fontWeight: 600 }}>Dueno</th>
                      <th style={{ fontSize: "13px", color: COLORS.textMuted, fontWeight: 600 }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.pacientesSinRegresar.map(item => (
                      <tr key={item.id}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {item.mascotaFoto ? (
                              <img
                                src={item.mascotaFoto}
                                alt={item.mascotaNombre}
                                style={{ width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover" }}
                              />
                            ) : (
                              <div style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                backgroundColor: COLORS.infoLight,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "14px"
                              }}>
                                🐾
                              </div>
                            )}
                            <span style={{ fontWeight: 500, color: COLORS.textDark }}>{item.mascotaNombre}</span>
                          </div>
                        </td>
                        <td style={{ color: COLORS.textDark, fontSize: "14px" }}>
                          {formatearFecha(item.ultimaVisita)}
                        </td>
                        <td style={{ color: COLORS.textMuted, fontSize: "14px" }}>
                          {item.tipoUltimaVisita}
                        </td>
                        <td style={{ color: COLORS.textMuted, fontSize: "14px" }}>
                          {item.duenoNombre}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm"
                            style={{
                              backgroundColor: COLORS.info,
                              color: "white",
                              fontSize: "12px",
                              padding: "4px 12px"
                            }}
                            onClick={() => handleProponerCita({
                              objetoId: item.objetoId,
                              mascotaNombre: item.mascotaNombre,
                              duenoId: item.duenoId,
                              duenoNombre: item.duenoNombre,
                              tipoSugerido: item.tipoUltimaVisita || "Consulta"
                            })}
                          >
                            Proponer cita
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal para proponer cita */}
      <ProponerCitaModal
        isOpen={showProponerModal}
        onClose={() => setShowProponerModal(false)}
        vetId={vetId}
        paciente={pacienteSeleccionado}
        onSuccess={handleCitaPropuesta}
      />
    </div>
  );
}
